import { PlanTrailActionContext, getMap, getLayerRegistry, safeRemoveLayer } from "./types";
import { LineStringFeature } from "../../../../../utils/models/LineStringFeature";
import { MarkerFeature } from "../../../../../utils/models/MarkerFeature";
import { snapPoints } from "../../../../../utils/models/Graphooper";
import { hiikerElevation } from "../../../../../utils/helpers/HiikerElevationApiHelper";
import { knownMarkerElevation } from "../../../../../utils/elevation/repairElevations";
import { plainToClass } from "class-transformer";
import { MapLayerCategory } from "../../../../../utils/layer-registry";
import mapboxgl from "mapbox-gl";
import { HoverOptimizationConfig } from "../../../services/HoverOptimizationConfig";
import { ROUTE_STYLES } from "../../../constants/route-styles";
import { getRouteStyleSettings, buildRouteStyleExpressions } from "../../../../../utils/routeStyleSettings";

export const polylineActions = {
  // Legacy function - removed. Use addAllRoutesToMap instead.

  /**
   * REFACTOR:
   * 1. Immediately return a provisional line (straight line)
   * 2. Kick off the real fetch in the background; replace it once done
   */
  async createPolylineGeoJson(
    { state, dispatch }: PlanTrailActionContext,
    { startMarker, endMarker }: { startMarker: MarkerFeature; endMarker: MarkerFeature }
  ): Promise<LineStringFeature> {
    // Check if markers are valid
    if (!startMarker || !endMarker || !startMarker.markerGeometry || !endMarker.markerGeometry) {
      console.error('Invalid markers passed to createPolylineGeoJson', { startMarker, endMarker });
      // Return empty linestring
      return LineStringFeature.coordsOnlyFeature([]);
    }

    // 1) provisional straight line
    const provisional = LineStringFeature.straightLine(startMarker, endMarker);

    // 2) background real-line fetch
    if (endMarker.markerProperties.isSnap) {
      // snapped network route
      snapPoints(startMarker, endMarker, state.routePreference)
        .then(async (snappedLine: LineStringFeature) => {
          let coords = snappedLine.lineStringGeometry.coordinates;

          // if previous marker was non-snap, prepend its exact click point
          if (!startMarker.markerProperties.isSnap) {
            // Never a placeholder 0: if the marker's lookup failed, borrow the elevation of
            // the snapped point it joins, which is metres away.
            const ele = knownMarkerElevation(startMarker) ?? coords[0]?.[2];
            const lon = startMarker.markerGeometry.lon();
            const lat = startMarker.markerGeometry.lat();
            coords = [Number.isFinite(ele) ? [lon, lat, ele] : [lon, lat], ...coords];
          }

          // Add code to modify surfaces

          snappedLine.lineStringGeometry.coordinates = coords;

          // move the new marker to the snapped endpoint
          await dispatch("updateSnappedMarker", {
            marker: endMarker,
            newCoords: coords[coords.length - 1]
          });

          // swap line
          dispatch("replaceProvisionalLine", {
            provisionalLine: provisional,
            realLine: snappedLine
          });
        })
        .catch(err => console.error("snapPoints error:", err));
    } else {
      // straight route with real elevations
      hiikerElevation(startMarker, endMarker)
        .then((elevLine: LineStringFeature) => {
          dispatch("replaceProvisionalLine", {
            provisionalLine: provisional,
            realLine: elevLine
          });
        })
        .catch(err => console.error("hiikerElevation error:", err));
    }

    return provisional;
  },

  /**
   * REFACTOR:
   * Replaces the provisional line with the real line once data arrives
   */
  async replaceProvisionalLine(
    { state, commit, dispatch, getters }: PlanTrailActionContext,
    {
      provisionalLine,
      realLine
    }: { provisionalLine: LineStringFeature; realLine: LineStringFeature }
  ) {
    console.log('[POLYLINES] replaceProvisionalLine called', {
      provisionalId: provisionalLine.lineStringProperties.id,
      pendingBefore: state.pendingProvisionalReplacements.size,
      pendingIds: Array.from(state.pendingProvisionalReplacements)
    });

    const activeRouteLineStrings = getters.activeRoute.lineStrings;
    const idx = activeRouteLineStrings.findIndex(
      ls => ls.lineStringProperties.id === provisionalLine.lineStringProperties.id
    );
    if (idx < 0) {
      console.log('[POLYLINES] replaceProvisionalLine - provisional line not found in route');
      return;
    }

    // remove old and insert new
    commit("removeLineStringAtIndex", idx);
    commit("addLineStringAtIndex", { lineString: realLine, index: idx });

    // Update the command-pattern undo manager so the stored linestring
    // reflects the real snapped route instead of the provisional straight line
    const provisionalId = provisionalLine.lineStringProperties.id;
    state.undoManager.updateProvisionalLine(provisionalId, realLine);

    // Legacy tracking for old snapshot-based undo (still needed during migration)
    const wasTracking = state.pendingProvisionalReplacements.has(provisionalId);
    state.pendingProvisionalReplacements.delete(provisionalId);

    console.log('[POLYLINES] After delete, pending size:', state.pendingProvisionalReplacements.size, 'wasTracking:', wasTracking);

    if (wasTracking && state.pendingProvisionalReplacements.size === 0) {
      console.log('[POLYLINES] All provisional lines replaced after marker drag, saving undo state');
      await commit("updateUndo");
    }

    // Only update route polylines — markers and hover are unchanged
    await dispatch("updateRouteSources");
  },

  async updateSnappedMarker(
    { commit, dispatch }: PlanTrailActionContext,
    { marker, newCoords }: { marker: MarkerFeature; newCoords: [number, number, number?] }
  ) {
    marker.markerGeometry.coordinates = newCoords;
    marker.markerProperties.isSnap = true;
    await commit("updateMarker", marker);
    // Only update markers — route lines will be updated by updateLineStringsFromMarkerMove
    await dispatch("updateMarkerSource");
  },

  async updateLineStringsFromMarkerMove({ state, commit, dispatch, getters }: PlanTrailActionContext, markerIndex: number) {
    console.log('[POLYLINES] updateLineStringsFromMarkerMove called, markerIndex:', markerIndex);

    const activeMarkers = getters.activeRoute.markers;
    let indexes: number[] = [];
    if (markerIndex == 0) {
      indexes.push(0);
    } else if (markerIndex == activeMarkers.length - 1) {
      indexes.push(markerIndex - 1);
    } else {
      indexes.push(markerIndex - 1);
      indexes.push(markerIndex);
    }

    console.log('[POLYLINES] Will update linestring indexes:', indexes);

    // Clear pending replacements - we're not tracking for undo anymore
    // Undo is saved BEFORE markerMoved is called
    state.pendingProvisionalReplacements.clear();

    for (let i = 0; i < indexes.length; i++) {
      const index1 = indexes[i];
      commit('removeLineStringAtIndex', index1);
      const startMarker = activeMarkers[index1];
      const endMarker = activeMarkers[index1 + 1];

      // Check if both markers exist before creating linestring
      if (startMarker && endMarker) {
        // Create a provisional line, let background fetch replace it
        const provisionalLine = await dispatch('createPolylineGeoJson', { startMarker, endMarker });
        commit('addLineStringAtIndex', { lineString: provisionalLine, index: index1 });
        console.log('[POLYLINES] Created provisional line (not tracked for undo):', provisionalLine.lineStringProperties.id);
      }
    }

    // Only update route polylines — markers already moved visually during drag
    await dispatch("updateRouteSources");
  },

  async addToPolyline(
    { state, dispatch, commit, getters }: PlanTrailActionContext,
    marker: MarkerFeature
  ) {
    console.log('[POLYLINES] addToPolyline called');

    const activeRoute = getters.activeRoute;
    if (!activeRoute) return;

    const n = activeRoute.markers.length;
    if (n < 2) return;

    const prev = activeRoute.markers[n - 2];

    // Clear and track the provisional line for this new segment
    state.pendingProvisionalReplacements.clear();
    console.log('[POLYLINES] Cleared pending provisional replacements');

    // 1) provisional
    const provisional = await dispatch("createPolylineGeoJson", {
      startMarker: prev,
      endMarker: marker
    });

    commit("addLineStringAtIndex", {
      lineString: provisional,
      index: activeRoute.lineStrings.length
    });

    // Track this provisional line so undo is saved when it's replaced
    state.pendingProvisionalReplacements.add(provisional.lineStringProperties.id);
    console.log('[POLYLINES] Added provisional line to tracking for addToPolyline:', provisional.lineStringProperties.id);
    console.log('[POLYLINES] Total pending:', state.pendingProvisionalReplacements.size);

    // Fallback: If GraphHopper doesn't respond within 5 seconds, save undo anyway
    setTimeout(() => {
      if (state.pendingProvisionalReplacements.size > 0) {
        console.log('[POLYLINES] Timeout reached for addToPolyline, saving undo state');
        state.pendingProvisionalReplacements.clear();
        commit('updateUndo');
      }
    }, 5000);

    // Update both routes and markers — new linestring + marker icons may change (first/last)
    await dispatch("updateRouteAndMarkers");
  },

  // Legacy function - removed. Use addAllRoutesToMap instead.

  // Legacy function - removed. Use addAllRoutesToMap instead.

  // Legacy function - removed. Use addAllRoutesToMap instead.

  // Legacy function - removed. Use createRouteLineLayer instead.

  // Legacy function - removed. Arrow layers are now handled in consolidated route rendering.

  // Consolidate all route line strings into a single FeatureCollection
  async addConsolidatedRoutesToMap({ state, dispatch }: PlanTrailActionContext) {
    const map = getMap();
    const layerRegistry = getLayerRegistry();

    if (!state.routes || Object.keys(state.routes).length === 0) {
      return;
    }

    // Create separate FeatureCollections for active and inactive line strings
    const activeFeatures: any[] = [];
    const inactiveFeatures: any[] = [];
    const activeRouteKey = state.activeRouteKey;

    // Collect all line string features from all routes
    for (const [routeKey, route] of Object.entries(state.routes)) {
      const isActive = routeKey === activeRouteKey;

      route.lineStrings.forEach((lineString, lineIndex) => {
        try {
          // Convert plain object to LineStringFeature instance
          const lineStringInstance = plainToClass(LineStringFeature, lineString);
          const feature = lineStringInstance.toGeoJson();
          // Add properties for styling and identification
          feature.properties = {
            ...feature.properties,
            routeId: route.id,
            lineIndex: lineIndex,
            routeKey: routeKey,
            routeStyle: route.route_style || 'route'
          };
          if (isActive) {
            activeFeatures.push(feature);
          } else {
            inactiveFeatures.push(feature);
          }
        } catch (error) {
          console.error(`Error processing line string ${lineIndex} for route ${routeKey}:`, error);
        }
      });
    }

    // If nothing to display, clear existing sources if present and exit
    if (activeFeatures.length === 0 && inactiveFeatures.length === 0) {
      const inactiveSourceId = 'consolidated-inactive-routes-lines';
      const activeSourceId = 'consolidated-active-routes-lines';
      const emptyFC = { type: 'FeatureCollection', features: [] } as any;
      const inactiveExisting = map.getSource(inactiveSourceId) as mapboxgl.GeoJSONSource | undefined;
      const activeExisting = map.getSource(activeSourceId) as mapboxgl.GeoJSONSource | undefined;
      if (inactiveExisting) { try { inactiveExisting.setData(emptyFC); } catch (_) {} }
      if (activeExisting) { try { activeExisting.setData(emptyFC); } catch (_) {} }
      return;
    }

    // IDs for split sources/layers
    const inactiveSourceId = 'consolidated-inactive-routes-lines';
    const inactiveLineLayerId = 'consolidated-inactive-routes-lines';
    const activeSourceId = 'consolidated-active-routes-lines';
    const activeLineLayerId = 'consolidated-active-routes-lines';
    const activeHoverLayerId = 'consolidated-active-routes-hover';
    const activeArrowLayerId = 'consolidated-active-routes-arrows';

    // Ensure sources exist and update via setData to avoid flashing
    const inactiveFC = { type: 'FeatureCollection', features: inactiveFeatures } as any;
    const activeFC = { type: 'FeatureCollection', features: activeFeatures } as any;

    const inactiveExisting = map.getSource(inactiveSourceId) as mapboxgl.GeoJSONSource | undefined;
    const activeExisting = map.getSource(activeSourceId) as mapboxgl.GeoJSONSource | undefined;

    if (inactiveExisting) {
      try { inactiveExisting.setData(inactiveFC); } catch (e) { console.warn('setData inactive failed; re-adding source'); layerRegistry.addSource(inactiveSourceId, { type: 'geojson', data: inactiveFC }, true); }
    } else {
      layerRegistry.addSource(inactiveSourceId, { type: 'geojson', data: inactiveFC }, true);
    }

    if (activeExisting) {
      try { activeExisting.setData(activeFC); } catch (e) { console.warn('setData active failed; re-adding source'); layerRegistry.addSource(activeSourceId, { type: 'geojson', data: activeFC }, true); }
    } else {
      layerRegistry.addSource(activeSourceId, { type: 'geojson', data: activeFC }, true);
    }

    // Create color expression based on route style
    const colorExpression: any = [
      'case',
      ['==', ['get', 'routeStyle'], 'route'], ROUTE_STYLES.route.color,
      ['==', ['get', 'routeStyle'], 'alt'], ROUTE_STYLES.alt.color,
      ['==', ['get', 'routeStyle'], 'spur'], ROUTE_STYLES.spur.color,
      ['==', ['get', 'routeStyle'], 'transport'], ROUTE_STYLES.transport.color,
      ROUTE_STYLES.route.color // default
    ];

    // Create border color expression based on route style
    const borderColorExpression: any = [
      'case',
      ['==', ['get', 'routeStyle'], 'route'], ROUTE_STYLES.route.borderColor,
      ['==', ['get', 'routeStyle'], 'alt'], ROUTE_STYLES.alt.borderColor,
      ['==', ['get', 'routeStyle'], 'spur'], ROUTE_STYLES.spur.borderColor,
      ['==', ['get', 'routeStyle'], 'transport'], ROUTE_STYLES.transport.borderColor,
      ROUTE_STYLES.route.borderColor // default
    ];

    // Create dash array expression for transport style
    const dashArrayExpression: any = [
      'case',
      ['==', ['get', 'routeStyle'], 'transport'],
      ['literal', ROUTE_STYLES.transport.dasharray || [1, 0]],
      ['literal', [1, 0]] // solid line for non-transport styles
    ];

    // Add inactive border layer (thicker, low opacity) - exclude transport style
    const inactiveBorderLayerId = 'consolidated-inactive-routes-borders';
    if (!map.getLayer(inactiveBorderLayerId)) {
      const inactiveBorderLayer: mapboxgl.LineLayer = {
        id: inactiveBorderLayerId,
        type: 'line',
        source: inactiveSourceId,
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': borderColorExpression,
          'line-width': 7, // Wider for border
          'line-opacity': [
            'case',
            ['==', ['get', 'routeStyle'], 'transport'],
            0, // No border for transport style
            0.4 // Border for other styles
          ],
          'line-dasharray': dashArrayExpression
        }
      };
      layerRegistry.addLayer(inactiveBorderLayer, MapLayerCategory.Annotations, { z: 7 });
    }

    // Add inactive line layer (thin, low opacity)
    if (!map.getLayer(inactiveLineLayerId)) {
      const inactiveLineLayer: mapboxgl.LineLayer = {
        id: inactiveLineLayerId,
        type: 'line',
        source: inactiveSourceId,
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': colorExpression,
          'line-width': 3,
          'line-opacity': 0.4,
          'line-dasharray': dashArrayExpression
        }
      };
      layerRegistry.addLayer(inactiveLineLayer, MapLayerCategory.Annotations, { z: 8 });
    }

    // Add active border layer (thicker, high opacity) - exclude transport style
    const activeBorderLayerId = 'consolidated-active-routes-borders';
    if (!map.getLayer(activeBorderLayerId)) {
      const activeBorderLayer: mapboxgl.LineLayer = {
        id: activeBorderLayerId,
        type: 'line',
        source: activeSourceId,
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': borderColorExpression,
          'line-width': 9, // Wider for border
          'line-opacity': [
            'case',
            ['==', ['get', 'routeStyle'], 'transport'],
            0, // No border for transport style
            1.0 // Border for other styles
          ],
          'line-dasharray': dashArrayExpression
        }
      };
      layerRegistry.addLayer(activeBorderLayer, MapLayerCategory.Annotations, { z: 8 });
    }

    // Add active line layer (thicker, high opacity)
    if (!map.getLayer(activeLineLayerId)) {
      const activeLineLayer: mapboxgl.LineLayer = {
        id: activeLineLayerId,
        type: 'line',
        source: activeSourceId,
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': colorExpression,
          'line-width': 5,
          'line-opacity': 1.0,
          'line-dasharray': dashArrayExpression
        }
      };
      layerRegistry.addLayer(activeLineLayer, MapLayerCategory.Annotations, { z: 9 });
    }

    // Add hover detection layer
    const currentZoom = map.getZoom();
    const hoverWidth = HoverOptimizationConfig.getHoverLayerWidth(currentZoom);

    if (!map.getLayer(activeHoverLayerId)) {
      const hoverLayer: mapboxgl.LineLayer = {
        id: activeHoverLayerId,
        type: 'line',
        source: activeSourceId,
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#000000',
          'line-width': hoverWidth,
          'line-opacity': 0.01
        }
      };
      layerRegistry.addLayer(hoverLayer, MapLayerCategory.Annotations, { z: 11 });
    }

    // Add arrow layer for active routes using sprite sheet image
    if (!map.getLayer(activeArrowLayerId)) {
      const arrowLayer = {
        id: activeArrowLayerId,
        type: 'symbol',
        source: activeSourceId,
        layout: {
          'symbol-placement': 'line',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-image': 'hike-arrow-icon',
          'icon-size': 0.2,
          'visibility': 'visible'
        }
      } as any;
      layerRegistry.addLayer(arrowLayer, MapLayerCategory.Annotations, { z: 11 });
    }

    // Apply custom route style settings (color, thickness, opacity) from localStorage
    const customExprs = buildRouteStyleExpressions(getRouteStyleSettings());
    if (map.getLayer(inactiveBorderLayerId)) {
      map.setPaintProperty(inactiveBorderLayerId, 'line-color', customExprs.borderColorExpr as any);
      map.setPaintProperty(inactiveBorderLayerId, 'line-width', customExprs.inactiveBorderWidthExpr as any);
      map.setPaintProperty(inactiveBorderLayerId, 'line-opacity', customExprs.inactiveBorderOpacityExpr as any);
    }
    if (map.getLayer(inactiveLineLayerId)) {
      map.setPaintProperty(inactiveLineLayerId, 'line-color', customExprs.colorExpr as any);
      map.setPaintProperty(inactiveLineLayerId, 'line-width', customExprs.inactiveLineWidthExpr as any);
      map.setPaintProperty(inactiveLineLayerId, 'line-opacity', customExprs.inactiveLineOpacity);
    }
    if (map.getLayer(activeBorderLayerId)) {
      map.setPaintProperty(activeBorderLayerId, 'line-color', customExprs.borderColorExpr as any);
      map.setPaintProperty(activeBorderLayerId, 'line-width', customExprs.activeBorderWidthExpr as any);
      map.setPaintProperty(activeBorderLayerId, 'line-opacity', customExprs.activeBorderOpacityExpr as any);
    }
    if (map.getLayer(activeLineLayerId)) {
      map.setPaintProperty(activeLineLayerId, 'line-color', customExprs.colorExpr as any);
      map.setPaintProperty(activeLineLayerId, 'line-width', customExprs.activeLineWidthExpr as any);
      map.setPaintProperty(activeLineLayerId, 'line-opacity', customExprs.activeLineOpacityExpr as any);
    }

    // Update hover width on zoom changes
    if (!state._consolidatedHoverZoomHandler) {
      state._consolidatedHoverZoomHandler = () => {
        const newZoom = map.getZoom();
        const newWidth = HoverOptimizationConfig.getHoverLayerWidth(newZoom);

        if (map.getLayer(activeHoverLayerId)) {
          map.setPaintProperty(activeHoverLayerId, 'line-width', newWidth);
        }
      };

      map.on('zoomend', state._consolidatedHoverZoomHandler);
    }
  },

  // Route-specific line string actions
  async addRouteLineStringToMap({ state, dispatch }: PlanTrailActionContext, data: {
    lineStringFeature: LineStringFeature,
    routeId: string,
    lineIndex: number,
    isActive: boolean
  }) {
    const map = getMap();
    const layerRegistry = getLayerRegistry();
    const { routeId, lineIndex, isActive } = data;

    // Remove existing layer if it exists
    await dispatch("removeRouteLineStringFromMap", { routeId, lineIndex });

    const stringId = `route-${routeId}-line-${lineIndex}`;
    const hoverLayerId = `route-${routeId}-line-${lineIndex}-hover`;
    const arrowLayer = `route-${routeId}-arrow-${lineIndex}`;

    const lineLayer = await dispatch("createRouteLineLayer", {
      stringId,
      isActive
    });

    // Add source with replace flag to ensure clean state
    const lineString = plainToClass(LineStringFeature, data.lineStringFeature);
    layerRegistry.addSource(stringId, lineString.toGeoJsonSource(), true);

    // Add the visible line layer
    layerRegistry.addLayer(lineLayer, MapLayerCategory.Annotations, { z: isActive ? 10 : 8 });

    // Add invisible hover detection layer for active routes
    if (isActive) {
      // Get current zoom level for dynamic hover width
      const currentZoom = map.getZoom();
      const hoverWidth = HoverOptimizationConfig.getHoverLayerWidth(currentZoom);

      // Create a wider invisible line for hover detection
      const hoverLayer = {
        id: hoverLayerId,
        type: "line",
        source: stringId,
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": "transparent",
          "line-width": hoverWidth, // Dynamic width based on zoom
          "line-opacity": 0
        }
      };
      layerRegistry.addLayer(hoverLayer, MapLayerCategory.Annotations, { z: 11 });

      // Update hover width on zoom changes
      if (!state._hoverZoomHandler) {
        state._hoverZoomHandler = () => {
          const newZoom = map.getZoom();
          const newWidth = HoverOptimizationConfig.getHoverLayerWidth(newZoom);

          // Update all hover layers
          if (state.routes) {
            Object.keys(state.routes).forEach(routeId => {
              const route = state.routes[routeId];
              route.lineStrings.forEach((_, idx) => {
                const layerId = `route-${routeId}-line-${idx}-hover`;
                if (map.getLayer(layerId)) {
                  map.setPaintProperty(layerId, 'line-width', newWidth);
                }
              });
            });
          }
        };

        map.on('zoomend', state._hoverZoomHandler);
      }

      await dispatch('addPolylineArrowLayerToMap', {
        targetLayerId: stringId,
        arrowLayer
      });
    }
  },

  async removeRouteLineStringFromMap({ }: PlanTrailActionContext, data: { routeId: string, lineIndex: number }) {
    const { routeId, lineIndex } = data;
    const stringId = `route-${routeId}-line-${lineIndex}`;
    const hoverLayerId = `route-${routeId}-line-${lineIndex}-hover`;
    const arrowLayer = `route-${routeId}-arrow-${lineIndex}`;

    // Remove hover layer first (if it exists)
    safeRemoveLayer(hoverLayerId);

    // Remove arrow layer (if it exists)
    safeRemoveLayer(arrowLayer);

    // Remove main line layer and source
    safeRemoveLayer(stringId);
  },

  createRouteLineLayer({ }: PlanTrailActionContext, data: { stringId: string, isActive: boolean }): mapboxgl.LineLayer {
    const { stringId, isActive } = data;
    return {
      id: stringId,
      type: "line",
      source: stringId,
      layout: {
        "line-join": "round",
        "line-cap": "round",
      },
      paint: {
        "line-color": isActive ? "#fd5372" : "#fd5372",
        "line-width": isActive ? 5 : 3,
        "line-opacity": isActive ? 1.0 : 0.4,
      },
    };
  }
};
